import React, { useState, useEffect } from "react";
import { Modal } from "react-bootstrap";
import editorange from "../assets/images/icon/edit-orange.png";
import Input from "components/Input";
import Select from "components/Select";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { updateEarningCode } from "services/earningCodes";

const EARNING_TYPE_OPTIONS = [
    { value: 'Regular', label: 'Regular' },
    { value: 'Overtime', label: 'Overtime' },
    { value: 'DoubleOvertime', label: 'Double Overtime' },
    { value: 'Holiday', label: 'Holiday' },
    { value: 'PTO', label: 'PTO' },
    { value: 'Sick', label: 'Sick' },
    { value: 'Bonus', label: 'Bonus' },
    { value: 'Commission', label: 'Commission' },
    { value: 'Reimbursement', label: 'Reimbursement' },
    { value: 'Severance', label: 'Severance' },
    { value: 'Other', label: 'Other' },
];

const buildForm = (c) => ({ name: c?.name || '', type: c?.type || '', active: c?.active ?? true });

function EarningCodeDetailsModal({ code, open, onClose, defaultEditing = false }) {
    const [isEditing, setIsEditing] = useState(defaultEditing);
    const [form, setForm] = useState(buildForm(code));
    const [current, setCurrent] = useState(code);
    const queryClient = useQueryClient();

    useEffect(() => {
        if (code) { setCurrent(code); setForm(buildForm(code)); setIsEditing(defaultEditing); }
    }, [code, defaultEditing]);

    const mutation = useMutation({
        mutationFn: updateEarningCode,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['earning-codes'] });
            setCurrent(prev => ({ ...prev, name: form.name, type: form.type, active: form.active }));
            setIsEditing(false);
        },
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
    };

    const handleSave = (e) => {
        e.preventDefault();
        mutation.mutate({ codeId: current.id, name: form.name, type: form.type, active: form.active });
    };

    const handleClose = () => { mutation.reset(); onClose(); };
    if (!current) return null;

    return (
        <Modal className='add-workplace-modal' show={open} onHide={handleClose} centered>
            <div className="modal-header border-0 pb-0 d-flex justify-content-between align-items-center">
                <h4 className="sub-title mb-0">Earning Code Detail</h4>
                <button type="button" className="close bg-transparent border-0" onClick={handleClose}>
                    <span aria-hidden="true">&times;</span>
                </button>
            </div>
            <Modal.Body>
                {!isEditing ? (
                    <>
                        <div className='d-flex justify-content-between align-items-center'>
                            <div>
                                <p className='gray-title my-2'>Code Id</p>
                                <h6 className='id-title fw-medium text-black m-0'>{current.id}</h6>
                            </div>
                            <img className='cursor-pointer' src={editorange} alt="edit" onClick={() => setIsEditing(true)} />
                        </div>
                        <div className='border-bottom my-3 pb-4'>
                            <h2 className='sub-title'>General Information</h2>
                            <div className='d-flex workplace-detail-section mt-4'>
                                <div>
                                    <p className='gray-title my-2'>Name</p>
                                    <h6 className='id-title fw-medium text-black m-0'>{current.name || '—'}</h6>
                                </div>
                                <div>
                                    <p className='gray-title my-2'>Earning Type</p>
                                    <h6 className='id-title fw-medium text-black m-0'>{current.type || '—'}</h6>
                                </div>
                                <div>
                                    <p className='gray-title my-2'>Active Status</p>
                                    <button className='active-button py-1 px-4'>{current.active ? 'Active' : 'Inactive'}</button>
                                </div>
                            </div>
                        </div>
                    </>
                ) : (
                    <form onSubmit={handleSave}>
                        <div className='row mt-3'>
                            <div className='col-12'>
                                <Input label="Name" fullWidth variant='flat' name='name' value={form.name} onChange={handleChange} />
                            </div>
                            <div className='col-12 mt-3'>
                                <Select label="Earning Type" fullWidth placeholder='Select Type' options={EARNING_TYPE_OPTIONS} name='type' value={form.type} onChange={handleChange} />
                            </div>
                            <div className='col-12 mt-3 d-flex align-items-center gap-3'>
                                <label className='gray-title mb-0'>Active Status</label>
                                <div className="form-check form-switch mb-0">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        checked={form.active}
                                        onChange={e => setForm(prev => ({ ...prev, active: e.target.checked }))}
                                    />
                                    <label className="form-check-label">{form.active ? 'Active' : 'Inactive'}</label>
                                </div>
                            </div>
                        </div>
                        {mutation.isError && (
                            <p className="text-danger mt-3">
                                {mutation.error?.response?.data?.message || mutation.error?.message || 'Failed to update earning code. Please try again.'}
                            </p>
                        )}
                        <div className='d-flex gap-2 mt-4'>
                            <button type='submit' className='button-orange py-2 px-4 rounded-pill' disabled={mutation.isPending}>
                                {mutation.isPending ? 'Saving...' : 'Save Changes'}
                            </button>
                            <button type='button' className='btn btn-outline-secondary rounded-pill py-2 px-4' onClick={() => setIsEditing(false)} disabled={mutation.isPending}>
                                Cancel
                            </button>
                        </div>
                    </form>
                )}
            </Modal.Body>
        </Modal>
    );
}

export default EarningCodeDetailsModal;
